import { Fragment, useMemo, useState, type ReactNode } from 'react';
import {
  compareStandings,
  summariseComparison,
  type PointsMovement,
  type StandingsComparison,
} from './compare';
import type { PointsScope } from './definition';
import { PointsBreakdownPanel, type PointsBreakdownState } from './PointsBreakdown';
import { ScopeEditor, type ScopeEventOption } from './ScopeEditor';
import { diagnoseEmptyScope } from './scopeDiagnosis';
import { Pill } from './controls';
import { scopeSummary } from './wording';
import type { DefinitionValidation } from './validate';

/**
 * §9.1's right-hand pane and §9.8's test mode: who would have won, and over which events.
 *
 * <b>This is what makes the screen a design tool rather than a config form.</b> An operator
 * tuning a system worth tens of thousands in prizes is not checking that a form validates; they
 * are asking whether the shape they just built produces a board they would defend. So the
 * standings are the point of the pane and everything else on it is subordinate — the counts, the
 * warnings, the freshness marker.
 *
 * <b>The test scope lives here, not on the left (§3.2, §9.8).</b> A points system answers only how
 * a finish becomes points; which tournaments get scored is a property of the leaderboard using it,
 * and that is what makes one system reusable across seasons. But an operator still needs some set
 * of results to score while designing, without creating a leaderboard first — so the preview pane
 * owns its own scope, in §9.8's order: room, then the scope collapsed behind a summary, then the
 * live count, then compare. It persists to `PointsSystemVersion.PreviewScope`, a column the engine
 * never sees.
 *
 * <b>Everything in this pane picks the test data; none of it edits the system.</b> That is the one
 * thing the pane has to make unmistakable, because an operator who suspects that widening a date
 * range has quietly changed what they are building will stop using the preview — and the preview
 * is half of what this screen is for. So it is said in three places rather than one: on the pane's
 * own subtitle, on a chip beside the Test scope heading that stays visible while it is collapsed,
 * and in §9.8's sentence in full when it is open.
 *
 * <b>The board is three columns and a drill-down.</b> Rank, player, points — what somebody scans a
 * standings list for — and everything else about one player behind their own row (§7.4's working,
 * fetched per player). A column is paid for by every row whether or not it is read; a drill-down is
 * paid for by the person who asked the question. It also keeps the pane legible at the width it
 * actually has, which is a third of the screen beside the definition being edited.
 *
 * Two behaviours worth not changing. A recalculation leaves the previous board on screen and
 * marks itself as working, because blanking a 200-row table on every keystroke makes the pane
 * unreadable exactly while it is being used. And nothing here is ever computed in the browser:
 * these numbers come from the same engine that will score the season, which is the only reason a
 * preview is worth looking at.
 */

/**
 * The preview response, declared structurally rather than imported.
 *
 * `@brush/ui` depends on React and nothing else — `Rundown` and `Ticker` set the same
 * precedent — so a component takes the shape it needs and the app passes the DTO straight in.
 */
export interface PointsPreviewStanding {
  rank: number;
  playerId: string;
  playerName: string;
  points: number;
  eventsScored: number;
  isTied: boolean;
}

export interface PointsPreviewResult {
  eventsInScope: number;
  playersRanked: number;
  computedAt: string;
  warnings: string[];
  rows: PointsPreviewStanding[];
}

export type PointsPreviewState =
  /** The definition has problems, so there is nothing worth asking the server about. */
  | { status: 'blocked' }
  /** Nothing to preview against yet — no room chosen, typically. */
  | { status: 'idle'; message: string }
  | { status: 'loading'; previous: PointsPreviewResult | null }
  | { status: 'error'; message: string; previous: PointsPreviewResult | null }
  | { status: 'ready'; result: PointsPreviewResult };

/** The published board §9.8 measures the draft against, once the server has scored it. */
export type PointsCompareState =
  /** Switched off. Nothing has been requested, which is what keeps compare off the rate limit. */
  | { status: 'off' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; result: PointsPreviewResult };

/**
 * §7.4's working for one player, opened from their row.
 *
 * <b>Fetched per player, on demand, and that is what keeps the list a list.</b> The engine computes
 * a breakdown for everybody on the way to the total, so the payload could carry all of them — and a
 * hundred players over twenty events is two thousand explanations, re-sent on a debounce while
 * somebody types. So the board answers "who is ahead" and a row answers "why", which is also why
 * the columns here are the three a reader scans by: everything else about one player is one click
 * away rather than in every row of a table nobody is reading across.
 *
 * The app owns the request; this pane owns which row is open. Same division as `compare`.
 */
export interface PointsBreakdownAccess {
  /** Whose working is open, or null. */
  openPlayerId: string | null;
  onOpen: (playerId: string | null) => void;
  state: PointsBreakdownState;
}

export interface PointsCompare {
  /**
   * Which version the draft is being measured against, in the short form the column header uses
   * ("v2"), plus the long form for the checkbox ("version 2, published 4 Aug 2026").
   *
   * Null when there is nothing to compare with, and `reason` then says why — the control is left
   * off the pane entirely rather than shown disabled, the way a platform row offers no Edit.
   */
  baseline: { short: string; long: string } | null;
  reason?: string;
  on: boolean;
  onToggle: (on: boolean) => void;
  state: PointsCompareState;
}

export function PointsPreviewPane({
  state,
  scope,
  onScopeChange,
  scopeValidation,
  tags,
  events,
  eventsTruncated = false,
  eventsFailed = false,
  decimalPlaces,
  compare,
  breakdown,
  readOnly = false,
  header,
}: {
  state: PointsPreviewState;
  /** §9.8's test scope. Not part of the system — see the note on this file. */
  scope: PointsScope;
  onScopeChange: (next: PointsScope) => void;
  scopeValidation: DefinitionValidation;
  /** The room's tag vocabulary, for the scope's tag chips. */
  tags: readonly string[];
  events: readonly ScopeEventOption[] | null;
  eventsTruncated?: boolean;
  eventsFailed?: boolean;
  decimalPlaces: number;
  /** §9.8's compare affordance. Omitted where there is no catalogue behind the screen. */
  compare?: PointsCompare;
  /**
   * §7.4's per-player working. Omitted leaves the rows unexpandable rather than opening onto an
   * error — a screen with no fetcher behind it should not offer a control that cannot answer.
   */
  breakdown?: PointsBreakdownAccess;
  /**
   * Whether the caller can persist a test scope at all.
   *
   * <b>Not "is this version published".</b> §9.6 freezes `Definition` and explicitly nothing else,
   * and names disabling this pane on a published version as the single most likely misreading of
   * the freeze rule — which is what this prop used to be wired to. Testing an already-published
   * system against a different set of results is a normal thing to want, and it was reachable only
   * by cloning the system to obtain a draft.
   *
   * What does close these controls is having no write path: a room looking at a *platform* system
   * (§2.2) cannot save anything here, and an editable control with nowhere to save to would take
   * an operator's change and silently drop it — the same reason a matched player's name is not
   * editable in the results grid.
   */
  readOnly?: boolean;
  /** App-supplied controls above the board — which room to test against, chiefly. */
  header?: ReactNode;
}) {
  const shown = resultOf(state);
  const stale = state.status === 'loading' || state.status === 'error';

  const against = compare?.on && compare.state.status === 'ready' ? compare.state.result : null;
  const comparison = useMemo(
    () => (shown && against ? compareStandings(shown, against) : null),
    [shown, against],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-b border-line px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="font-display text-sm font-semibold text-ink-950">Standings preview</h2>
          {state.status === 'loading' ? (
            <span className="text-[11px] text-ink-800/45">Recalculating…</span>
          ) : shown ? (
            <span className="num text-[11px] text-ink-800/40">
              {new Date(shown.computedAt).toLocaleTimeString()}
            </span>
          ) : null}
        </div>
        {/* Not "nothing here is saved" — the test scope is stored on the version, so that would
            be untrue. What is true, and is the thing an operator needs to trust, is that choosing
            different results to score changes nothing about the system being built. */}
        <p className="mt-0.5 text-[11.5px] text-ink-800/55">
          Your real results, scored by the definition on the left. Choosing what to test against
          never changes the definition.
        </p>
        {header ? <div className="mt-3">{header}</div> : null}
      </header>

      {/* §9.8's order: the room above, the scope here, then the live count and compare below. */}
      <TestScope
        scope={scope}
        onChange={onScopeChange}
        validation={scopeValidation}
        tags={tags}
        events={events}
        eventsTruncated={eventsTruncated}
        eventsFailed={eventsFailed}
        eventsInScope={shown?.eventsInScope ?? null}
        readOnly={readOnly}
      />

      {shown ? (
        <div className="flex flex-wrap gap-x-6 gap-y-1 border-b border-line px-5 py-3">
          <Figure label="Events in scope" value={shown.eventsInScope} />
          <Figure label="Players ranked" value={shown.playersRanked} />
        </div>
      ) : null}

      {compare ? (
        <CompareBar compare={compare} comparison={comparison} hasBoard={shown !== null} />
      ) : null}

      {shown && shown.warnings.length > 0 ? (
        <ul className="space-y-1 border-b border-line bg-warn/5 px-5 py-3">
          {shown.warnings.map((warning, i) => (
            <li key={i} className="flex gap-1.5 text-[11.5px] text-warn">
              <span aria-hidden>!</span>
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={`min-h-0 flex-1 overflow-y-auto ${stale ? 'opacity-60' : ''}`}>
        <Body
          state={state}
          shown={shown}
          scope={scope}
          events={events}
          eventsTruncated={eventsTruncated}
          eventsFailed={eventsFailed}
          decimalPlaces={decimalPlaces}
          comparison={comparison}
          baselineLabel={compare?.baseline?.short ?? null}
          breakdown={breakdown}
        />
      </div>
    </div>
  );
}

function Body({
  state,
  shown,
  scope,
  events,
  eventsTruncated,
  eventsFailed,
  decimalPlaces,
  comparison,
  baselineLabel,
  breakdown,
}: {
  state: PointsPreviewState;
  shown: PointsPreviewResult | null;
  scope: PointsScope;
  events: readonly ScopeEventOption[] | null;
  eventsTruncated: boolean;
  eventsFailed: boolean;
  decimalPlaces: number;
  /** Present only while compare is on and its board has come back. */
  comparison: StandingsComparison | null;
  baselineLabel: string | null;
  breakdown: PointsBreakdownAccess | undefined;
}) {
  if (state.status === 'blocked') {
    return (
      <Notice title="Fix the problems on the left">
        The definition has something the server would refuse, so there is nothing to score with
        yet. Standings come back as soon as it is valid.
      </Notice>
    );
  }

  if (state.status === 'idle') {
    return <Notice title="Nothing to score against yet">{state.message}</Notice>;
  }

  if (state.status === 'error' && !shown) {
    return <Notice title="Couldn’t work out the standings">{state.message}</Notice>;
  }

  if (!shown) {
    return <Notice title="Working it out…">Scoring your results with this definition.</Notice>;
  }

  if (shown.eventsInScope === 0) {
    return (
      <Notice title="No events are in scope">
        {diagnoseEmptyScope(scope, events, eventsTruncated, eventsFailed)}
      </Notice>
    );
  }

  if (shown.rows.length === 0) {
    return (
      <Notice title="No player scored anything">
        {shown.eventsInScope} event{shown.eventsInScope === 1 ? '' : 's'} are in scope, but nobody
        earned a point from them. Check the placement table — a place matching no row scores
        nothing.
      </Notice>
    );
  }

  return (
    <>
      {state.status === 'error' ? (
        <p role="alert" className="border-b border-line border-l-4 border-l-bad bg-surface px-5 py-2.5 text-[11.5px] text-ink-900">
          {state.message} The board below is from the last calculation that worked.
        </p>
      ) : null}

      {/* Three columns, and that is the whole list: who is where, and with what. Everything else
          about a player — which events they scored, what each finish was worth, why a chop moved
          them down a place — is behind their row rather than in a column, because this table is
          scanned for somebody and then read about them. */}
      <table className="w-full text-left text-sm">
        <thead className="sticky top-0 bg-surface text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
          <tr className="border-b border-line">
            <th className="w-10 px-5 py-2 text-right font-semibold">#</th>
            <th className="px-2 py-2 font-semibold">Player</th>
            {comparison ? (
              <th className="px-2 py-2 text-right font-semibold">
                vs {baselineLabel ?? 'published'}
              </th>
            ) : null}
            <th className="px-5 py-2 text-right font-semibold">Points</th>
          </tr>
        </thead>
        <tbody>
          {shown.rows.map((row) => {
            const open = breakdown?.openPlayerId === row.playerId;

            return (
              <Fragment key={row.playerId}>
                <tr
                  // The row is the control, so the whole of it responds — but the button in the
                  // player cell is what carries the semantics, and its keyboard activation reaches
                  // this handler by bubbling rather than by a second one that could disagree.
                  onClick={breakdown ? () => breakdown.onOpen(open ? null : row.playerId) : undefined}
                  className={`border-b border-line/60 last:border-b-0 ${
                    breakdown ? 'cursor-pointer hover:bg-ground/50' : ''
                  } ${open ? 'bg-ground/50' : ''}`}
                >
                  <td className="num px-5 py-1.5 text-right text-ink-800/60">
                    {row.rank}
                    {/* Tied players share a rank and the next one skips (1, 2, 2, 4). Marked,
                        because two identical numbers in a column otherwise read as a bug. */}
                    {row.isTied ? <span title="Tied">=</span> : null}
                  </td>
                  <td className="px-2 py-1.5 text-ink-950">
                    {breakdown ? (
                      <button
                        type="button"
                        aria-expanded={open}
                        className="flex w-full items-baseline gap-1.5 text-left"
                      >
                        <span aria-hidden className="text-[9px] text-ink-800/40">
                          {open ? '▾' : '▸'}
                        </span>
                        <span className="min-w-0 truncate">{row.playerName}</span>
                      </button>
                    ) : (
                      row.playerName
                    )}
                  </td>
                  {comparison ? (
                    <td className="px-2 py-1.5 text-right">
                      <Movement
                        movement={comparison.movements.get(row.playerId)}
                        decimalPlaces={decimalPlaces}
                      />
                    </td>
                  ) : null}
                  <td className="num px-5 py-1.5 text-right font-semibold text-ink-950">
                    {row.points.toFixed(decimalPlaces)}
                  </td>
                </tr>

                {open && breakdown ? (
                  <tr className="border-b border-line/60">
                    <td colSpan={comparison ? 4 : 3} className="p-0">
                      <PointsBreakdownPanel state={breakdown.state} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {/* The board is the whole field; only the list is cut. Saying so stops "why does it stop at
          100" being read as a scoring bug. */}
      {shown.playersRanked > shown.rows.length ? (
        <p className="px-5 py-3 text-[11px] text-ink-800/45">
          Showing the top {shown.rows.length} of {shown.playersRanked} ranked players.
        </p>
      ) : null}
    </>
  );
}

/**
 * §9.8's compare affordance — "this is how an operator judges whether an edit does what they
 * intended".
 *
 * <b>Off by default, and that is not a default chosen for tidiness.</b> Compare costs a second
 * call to the endpoint whose cost the caller sets, on a limit of 60 a minute; leaving it on would
 * halve the number of edits an operator can make before being refused. It is also a question
 * somebody asks at a particular moment — "is this better than what we published?" — rather than
 * something they read continuously.
 *
 * With nothing published there is no control at all, only the sentence saying why. A checkbox that
 * looks available and does nothing is worse than an absent one; the catalogue's platform rows make
 * the same choice about Edit.
 */
function CompareBar({
  compare,
  comparison,
  hasBoard,
}: {
  compare: PointsCompare;
  comparison: StandingsComparison | null;
  hasBoard: boolean;
}) {
  if (!compare.baseline) {
    return compare.reason ? (
      <p className="border-b border-line px-5 py-2.5 text-[11px] text-ink-800/45">
        {compare.reason}
      </p>
    ) : null;
  }

  return (
    <section className="border-b border-line px-5 py-2.5">
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          checked={compare.on}
          onChange={(e) => compare.onToggle(e.target.checked)}
          className="mt-0.5 accent-[var(--color-felt-700)]"
        />
        <span className="min-w-0">
          <span className="block text-[12.5px] text-ink-950">
            Compare with {compare.baseline.long}
          </span>
          <span className="mt-0.5 block text-[11px] leading-relaxed text-ink-800/55">
            Scores the same events both ways and shows where each player moves.
          </span>
        </span>
      </label>

      {compare.on ? (
        <div className="mt-2 border-l-2 border-line pl-2.5 text-[11.5px] leading-relaxed">
          {compare.state.status === 'error' ? (
            <span role="alert" className="text-bad">
              {compare.state.message}
            </span>
          ) : !hasBoard ? (
            // Ahead of `loading`, because with the definition invalid nothing was ever sent and
            // "scoring…" would be a claim about a request that does not exist.
            <span className="text-ink-800/50">
              Waiting for the draft’s own board before there is anything to compare.
            </span>
          ) : compare.state.status === 'loading' ? (
            // Deliberately not "scoring v2": the wait is usually the *draft's* board being worked
            // out after an edit, and naming the wrong side of the comparison invites the reader to
            // conclude the published version is somehow being recomputed.
            <span className="text-ink-800/50">Scoring both boards over the same events…</span>
          ) : comparison ? (
            <>
              <span className="text-ink-900">{summariseComparison(comparison)}</span>
              {comparison.left === null ? (
                // Both lists stop at the same place, so a player who fell off the *list* looks
                // exactly like one who fell off the *board*. Said, rather than counted wrongly.
                <span className="mt-0.5 block text-ink-800/45">
                  This board is longer than the list shown, so anyone below it isn’t compared.
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-ink-800/50">Nothing to compare yet.</span>
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * One player's rank movement.
 *
 * Green up and red down is the convention every standings board in the world already uses, so it
 * needs no legend — but the two cases that are *not* movement do need saying in words, because a
 * dash and a badge would otherwise both read as "didn't move". `new` is a player the published
 * version genuinely did not score; `unknown` is one whose old position is off the end of a
 * truncated list, which is a different fact and must not be dressed up as the first.
 */
function Movement({
  movement,
  decimalPlaces,
}: {
  movement: PointsMovement | undefined;
  decimalPlaces: number;
}) {
  if (!movement) return <span className="text-ink-800/30">—</span>;

  const was =
    movement.previousRank !== null && movement.previousPoints !== null
      ? `Was ${movement.previousRank} with ${movement.previousPoints.toFixed(decimalPlaces)}`
      : undefined;

  switch (movement.status) {
    case 'up':
      return (
        <span className="num text-[12px] font-semibold text-good" title={was}>
          ▲{movement.places}
        </span>
      );
    case 'down':
      return (
        <span className="num text-[12px] font-semibold text-bad" title={was}>
          ▼{Math.abs(movement.places ?? 0)}
        </span>
      );
    case 'level':
      // Same place either way; whether the *score* moved is in the tooltip rather than in a second
      // marker, because a column headed with a rank comparison should not start answering a
      // different question halfway down.
      return (
        <span
          className="num text-[12px] text-ink-800/40"
          title={
            movement.pointsDelta
              ? `Same place. Points ${movement.pointsDelta > 0 ? 'up' : 'down'} ${Math.abs(
                  movement.pointsDelta,
                ).toFixed(decimalPlaces)}.`
              : 'Same place, same points.'
          }
        >
          =
        </span>
      );
    case 'new':
      return (
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.06em] text-good"
          title="Scored nothing under the published version."
        >
          New
        </span>
      );
    default:
      return (
        <span
          className="text-[10px] uppercase tracking-[0.06em] text-ink-800/40"
          title="Below where the published version’s list stopped, so their old place isn’t known."
        >
          ?
        </span>
      );
  }
}

/**
 * §9.8's test scope: collapsed by default behind a summary line, expanding to the full editor.
 *
 * <b>Collapsed is the right default and the reason is not tidiness.</b> This is a test harness,
 * and the screen's subject is the definition on the left — a scope editor sitting permanently open
 * above the standings would read as part of what is being built, which is the exact confusion
 * §3.2 exists to end. The summary line keeps it honest: an operator can always see which events
 * they are looking at without opening anything.
 *
 * The label under it is the one §9.8 writes out, verbatim. It answers the question an operator
 * will otherwise ask support: "if I change these dates, does my system change?"
 */
function TestScope({
  scope,
  onChange,
  validation,
  tags,
  events,
  eventsTruncated,
  eventsFailed,
  eventsInScope,
  readOnly,
}: {
  scope: PointsScope;
  onChange: (next: PointsScope) => void;
  validation: DefinitionValidation;
  tags: readonly string[];
  events: readonly ScopeEventOption[] | null;
  eventsTruncated: boolean;
  eventsFailed: boolean;
  /** The server's count, or null before the first preview comes back. */
  eventsInScope: number | null;
  readOnly: boolean;
}) {
  // Opened for a scope with problems, because a collapsed section cannot show what is wrong with
  // it — and the preview stays blocked until it is fixed.
  const [open, setOpen] = useState(() => !validation.isValid);

  return (
    <section className="border-b border-line px-5 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          className="flex items-baseline gap-1.5 text-left"
        >
          <span className="text-[11px] uppercase tracking-[0.08em] text-ink-800/45">
            Test scope
          </span>
          {/* Visible collapsed as well as open, which is the point of it: §9.8's sentence below
              only appears once the section is expanded, and by then an operator who was going to
              worry has already worried. */}
          <Pill>Testing only</Pill>
          <span aria-hidden className="text-[10px] text-ink-800/40">
            {open ? '▾' : '▸'}
          </span>
        </button>

        {!open ? (
          <span className="num text-[11.5px] text-ink-800/60">{summarise(scope, eventsInScope)}</span>
        ) : null}
      </div>

      {open ? (
        <div className="mt-3 space-y-3">
          <ScopeEditor
            value={scope}
            onChange={onChange}
            tags={tags}
            events={events}
            eventsTruncated={eventsTruncated}
            eventsFailed={eventsFailed}
            validation={validation}
            readOnly={readOnly}
          />
          <p className="border-t border-line pt-2.5 text-[11px] leading-relaxed text-ink-800/55">
            Used to preview this system while you design it. Changing it never changes the
            definition on the left — it is stored beside the version and the scoring engine never
            reads it. A leaderboard that adopts this system defines its own events.
          </p>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The collapsed line: "12 events · Jul 28 – Aug 11".
 *
 * The count is the server's, so before the first preview returns it simply says the dates rather
 * than guessing at a number — a scope summary that quietly counted the events this screen happens
 * to have loaded would disagree with the figure below it, since the server counts re-entries the
 * tournament list does not report.
 *
 * <b>It delegates to `scopeSummary` rather than formatting its own.</b> It was a second
 * implementation of the same sentence, and the two agreed right up until a bound could be null:
 * this copy printed `null – null` for an open-ended scope where the other says "Every event, no
 * date limit". One sentence, one home.
 */
function summarise(scope: PointsScope, eventsInScope: number | null): string {
  return scopeSummary(scope, eventsInScope);
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="num font-figure text-lg font-semibold leading-none text-ink-950">
        {value.toLocaleString('en-US')}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-ink-800/45">{label}</div>
    </div>
  );
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-5 py-8">
      <p className="text-[13px] font-medium text-ink-950">{title}</p>
      <p className="mt-1.5 max-w-sm text-[12px] leading-relaxed text-ink-800/60">{children}</p>
    </div>
  );
}

function resultOf(state: PointsPreviewState): PointsPreviewResult | null {
  switch (state.status) {
    case 'ready':
      return state.result;
    case 'loading':
    case 'error':
      return state.previous;
    default:
      return null;
  }
}

/** The draft badge and friends, so the list and the builder label a version identically. */
export function VersionStatusPill({ status }: { status: string }) {
  switch (status) {
    case 'published':
      return <Pill tone="good">Published</Pill>;
    case 'archived':
      return <Pill tone="neutral">Archived</Pill>;
    case 'draft':
      return <Pill tone="warn">Draft</Pill>;
    default:
      return <Pill tone="neutral">{status}</Pill>;
  }
}
